import React, { useState } from "react";
import { Share2, Twitter, Linkedin, Facebook, Link2, Check } from "lucide-react";

/**
 * Social share buttons for Academy articles.
 * Opens each platform's share intent in a small popup and provides a copy-link fallback.
 *
 * All share URLs are built client-side against the current window.location.href, so they
 * automatically respect the deploy environment (preview or production) with no config.
 */

function openShareWindow(url) {
  const w = 600;
  const h = 500;
  const left = (window.screen.width - w) / 2;
  const top = (window.screen.height - h) / 2;
  window.open(url, "share", `noopener,noreferrer,width=${w},height=${h},top=${top},left=${left}`);
}

function ShareIconButton({ label, testid, onClick, children, tint = "bg-white/5 hover:bg-white/10 text-white/80" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border border-white/10 transition-colors text-xs font-medium ${tint}`}
      title={label}
      aria-label={label}
      data-testid={testid}
    >
      {children}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

export default function ShareBar({ title, summary, hashtags = "ANATRADEX,Trading,Quant" }) {
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title || "");
  const encodedSummary = encodeURIComponent(summary || "");
  const encodedHashtags = encodeURIComponent(hashtags);

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}&hashtags=${encodedHashtags}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`;
  const redditUrl = `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`;

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback for browsers that block async clipboard
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop — silent copy failure */ }
  };

  // Native Web Share API (mobile) — falls back to nothing if unsupported
  const nativeShare = async () => {
    if (!navigator.share) return copyLink();
    try {
      await navigator.share({ title, text: summary, url: shareUrl });
    } catch { /* user cancelled — noop */ }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 my-6 py-4 border-y border-white/10" data-testid="article-share-bar">
      <span className="text-xs uppercase tracking-widest text-white/40 mr-1 flex items-center gap-1.5">
        <Share2 size={12} /> Partager
      </span>

      <ShareIconButton
        label="X (Twitter)"
        testid="share-twitter"
        onClick={() => openShareWindow(twitterUrl)}
        tint="bg-black hover:bg-neutral-800 text-white border-neutral-700"
      >
        <Twitter size={13} />
      </ShareIconButton>

      <ShareIconButton
        label="LinkedIn"
        testid="share-linkedin"
        onClick={() => openShareWindow(linkedinUrl)}
        tint="bg-[#0a66c2]/90 hover:bg-[#0a66c2] text-white border-[#0a66c2]/50"
      >
        <Linkedin size={13} />
      </ShareIconButton>

      <ShareIconButton
        label="Facebook"
        testid="share-facebook"
        onClick={() => openShareWindow(facebookUrl)}
        tint="bg-[#1877f2]/90 hover:bg-[#1877f2] text-white border-[#1877f2]/50"
      >
        <Facebook size={13} />
      </ShareIconButton>

      <ShareIconButton
        label="Reddit"
        testid="share-reddit"
        onClick={() => openShareWindow(redditUrl)}
        tint="bg-[#ff4500]/90 hover:bg-[#ff4500] text-white border-[#ff4500]/50"
      >
        {/* Reddit icon isn't in lucide-react; use a simple monogram */}
        <span className="font-bold text-[11px] leading-none">R</span>
      </ShareIconButton>

      <ShareIconButton
        label={copied ? "Lien copié !" : "Copier le lien"}
        testid="share-copy-link"
        onClick={copyLink}
        tint={copied ? "bg-up/20 text-up border-up/40" : "bg-white/5 hover:bg-white/10 text-white/80"}
      >
        {copied ? <Check size={13} /> : <Link2 size={13} />}
      </ShareIconButton>

      {/* Mobile-only native share sheet — hidden on desktop where dedicated buttons are enough */}
      {typeof navigator !== "undefined" && navigator.share && (
        <ShareIconButton
          label="Plus"
          testid="share-native"
          onClick={nativeShare}
          tint="bg-white/5 hover:bg-white/10 text-white/80 sm:hidden"
        >
          <Share2 size={13} />
        </ShareIconButton>
      )}
    </div>
  );
}
